import React, { useEffect, useRef } from 'react';
import { STILL_TIME, buildPalette, drawOrb } from '../orb';

const SIZES = { launcher: 70, lg: 42, md: 32 };
const FRAME_MS_ACTIVE = 33;
const FRAME_MS_IDLE = 50;

const prefersReducedMotion = () => (
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
);

const FALLBACK_MID = [37, 99, 235];
const FALLBACK_DEEP = [30, 58, 138];

const parseColor = (css) => {
    const values = css.match(/-?\d*\.?\d+/g);
    if (!values || values.length < 3) return null;
    const scale = css.startsWith('color(') ? 255 : 1;
    return values.slice(0, 3).map((v) => Number(v) * scale);
};

function readPalette(anchor) {
    const probe = document.createElement('span');
    probe.style.display = 'none';
    anchor.appendChild(probe);
    const read = (token, fallback) => {
        probe.style.color = `var(${token}, rgb(${fallback.join(',')}))`;
        return parseColor(getComputedStyle(probe).color) || fallback;
    };
    const mid = read('--orb-mid', FALLBACK_MID);
    const deep = read('--orb-deep', FALLBACK_DEEP);
    const accentRaw = read('--orb-accent', [0, 0, 0]);
    const accent = (accentRaw[0] === 0 && accentRaw[1] === 0 && accentRaw[2] === 0) ? null : accentRaw;
    anchor.removeChild(probe);
    return buildPalette(mid, deep, accent);
}

export default function Logo({ size = 'md', animate = false, busy = false }) {
    const canvasRef = useRef(null);
    const busyRef = useRef(busy);
    busyRef.current = busy;
    const px = SIZES[size];

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas && canvas.getContext('2d');
        if (!ctx) return undefined;

        const ratio = Math.min(window.devicePixelRatio || 1, 3);
        canvas.width = px * ratio;
        canvas.height = px * ratio;
        ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
        const palette = readPalette(canvas.parentElement || document.body);

        if (!animate || prefersReducedMotion()) {
            drawOrb(ctx, px, STILL_TIME, 1, undefined, palette);
            return undefined;
        }

        let visible = true;
        const observer = typeof IntersectionObserver === 'function'
            ? new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; })
            : null;
        if (observer) observer.observe(canvas);

        const pointer = { over: false, x: 0, y: 0 };
        const lean = [0, 0];
        const host = canvas.closest('button') || canvas;
        const onEnter = () => { pointer.over = true; };
        const onLeave = () => { pointer.over = false; pointer.x = 0; pointer.y = 0; };
        const onMove = (event) => {
            const box = host.getBoundingClientRect();
            pointer.x = Math.max(-1, Math.min(1, (event.clientX - box.left - box.width / 2) / (box.width / 2)));
            pointer.y = Math.max(-1, Math.min(1, (event.clientY - box.top - box.height / 2) / (box.height / 2)));
        };
        host.addEventListener('pointerenter', onEnter);
        host.addEventListener('pointerleave', onLeave);
        host.addEventListener('pointermove', onMove);

        let frame = 0;
        let last = 0;
        let t = STILL_TIME;
        let energy = 1;
        const tick = (now) => {
            frame = requestAnimationFrame(tick);
            const interval = pointer.over || busyRef.current ? FRAME_MS_ACTIVE : FRAME_MS_IDLE;
            if (document.hidden || !visible || now - last < interval) return;
            const dt = last ? Math.min((now - last) / 1000, 0.1) : 0;
            last = now;
            const target = busyRef.current ? 2.6 : (pointer.over ? 1.9 : 1);
            energy += (target - energy) * Math.min(1, dt * 3);
            lean[0] += (pointer.x - lean[0]) * Math.min(1, dt * 6);
            lean[1] += (pointer.y - lean[1]) * Math.min(1, dt * 6);
            t += dt * energy;
            drawOrb(ctx, px, t, 1 + (energy - 1) * 0.4, lean, palette);
        };
        frame = requestAnimationFrame(tick);
        return () => {
            cancelAnimationFrame(frame);
            if (observer) observer.disconnect();
            host.removeEventListener('pointerenter', onEnter);
            host.removeEventListener('pointerleave', onLeave);
            host.removeEventListener('pointermove', onMove);
        };
    }, [animate, px]);

    return <canvas ref={canvasRef} className={`nova_logo nova_logo--${size}`} aria-hidden="true" />;
}
